import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DoaDetail } from '../../../interface/administrator/admin';
import { GroupedDoaDetail } from '../../../interface/administrator/admin';
import { AdminService } from '../../../services/administrator/admin.service';
import { IDoaMaster } from '../../../interface/administrator/admin';
import { CalAngularService } from '@cvx/cal-angular';
import { MaterialModule } from '../../../shared/module/material.module';
import { Router } from '@angular/router';
import { SharedServices } from '../../../shared/services/shared.services';
import { LookupComponent } from '../../../shared/component/look-up/lookup.component'; 

@Component({
  selector: 'app-employees-doa-setup',
  imports: [FormsModule, CommonModule, MaterialModule, LookupComponent],
  templateUrl: './employees-doa-setup.component.html',
  styleUrls: ['./employees-doa-setup.component.css'],
  standalone: true,
})
export class EmployeesDoaSetupComponent implements OnInit {
  email: string = '';
  query: string = '';
  employee: any = { employeeno: '', name: '', dept: '', effectiveDate: '' };
  searchResults: any[] = [];
  username: string = '';
  isUserVerified: boolean = false;
  showForm: boolean = false;
  showError: boolean = false;
  fetchDetails: boolean = false;
  signatureUrl: string = '';
  doaDetails: DoaDetail[] = [];
  groupedDoaDetails: GroupedDoaDetail[] = [];
  effectiveDate: string = '';
  ELEMENT_DATA: IDoaMaster[] = [];
  empNo: string = ''
  isLoading: boolean = false;
  loggedUserEmail: string = '';
  invalidEntries: { [key: string]: boolean } = {};
  effectiveDateError: boolean = false;
  initialEffectiveDate: string = '';
  initialDoaDetails: DoaDetail[] = [];

  constructor(private http: HttpClient, private router: Router, private adminService: AdminService, private authService: CalAngularService, private sharedService:SharedServices) { }

  ngOnInit(): void {
    this.loggedUserEmail = this.authService.cvxClaimsPrincipal.logOnName ?? '';
  }

  groupBy(array: DoaDetail[], keys: (keyof DoaDetail)[]): GroupedDoaDetail[] {
    const grouped: { [key: string]: GroupedDoaDetail } = {};
    array.forEach(item => {
      const key = keys.map(k => item[k]).join('|');
      if (!grouped[key]) {
        grouped[key] = { catName: item.catName, catTitle: item.catTitle, catCode: item.catCode, subCatName: '', noteCode: '', subCatCode: '', typeCode: '', types: [] };
      }
      const displayCode = item.noteCode || item.subCatNote;
      grouped[key].types.push({ noteCode: displayCode, subCatCode: item.subCatCode, subCatName: item.subCatName, typeName: item.typeName, typeCode: item.typeCode, dollar: item.dollar });
    });
    return Object.values(grouped);
  }

  SendData(): void {
    if (Object.values(this.invalidEntries).some(isInvalid => isInvalid)) {
      this.sharedService.openModal('Message', 'Please correct the invalid entries before submitting the request');
      return;
    }
    if (!this.effectiveDate) {
      this.effectiveDateError = true;
      this.sharedService.openModal('Message', 'Effective date is required');
      return;
    } else {
      this.effectiveDateError = false;
    }
  
    const effectiveDateChanged = this.effectiveDate !== this.initialEffectiveDate;
    const doaAmountsChanged = this.groupedDoaDetails.some((group) => {
      return group.types.some((type) => {
        const initialDetail = this.initialDoaDetails.find(d => 
          d.typeCode === type.typeCode && 
          d.catCode === group.catCode && 
          d.subCatCode === type.subCatCode
        );
        return initialDetail && type.dollar.trim().toUpperCase() !== initialDetail.dollar.trim().toUpperCase();
      });
    });
    if (!effectiveDateChanged && !doaAmountsChanged) {
      this.sharedService.openModal('Message', 'No changes detected');
      return;
    }
  
    const effectiveDateInput = this.effectiveDate;
    const effectiveDate = new Date(effectiveDateInput);
    const year = effectiveDate.getFullYear();
    const month = String(effectiveDate.getMonth() + 1).padStart(2, '0');
    const day = String(effectiveDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
  
    this.isLoading = true;
    this.ELEMENT_DATA = [];
    this.groupedDoaDetails.forEach((group) => {
      group.types.forEach((type) => {
        const selectedAmount = type.dollar.trim().toUpperCase();
        let formattedAmount = selectedAmount;
        if (!['FA', 'NA', 'R', 'VAR', ''].includes(selectedAmount)) {
          formattedAmount = Number(selectedAmount.replace(/,/g, '')).toLocaleString('en-US');
        }
        const newElement: IDoaMaster = {
          empNo: this.employee.employeeno,
          effectiveDate: formattedDate,
          typeCode: type.typeCode,
          catCode: group.catCode,
          subCatCode: type.subCatCode,
          dollar: formattedAmount,
          lastEditedByCai: this.loggedUserEmail,
          lastEditDate: new Date(),
        };
        this.ELEMENT_DATA.push(newElement);
      });
    });
  
    this.adminService.submitEmployeeDoaReq(this.ELEMENT_DATA).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.sharedService.openModal('Message', 'Employee DOA Request submitted successfully');
        this.resetForm();
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error sending data', error);
      }
    });
  }
    handleGoButtonClick(): void {
      this.isLoading = true;
      if (!this.isUserVerified) {
        if (this.query.trim() === '') {
          console.error('Query is empty');
          this.isLoading = false;
          this.sharedService.openModal('Message', 'Please enter a valid input');
          return;
        }
        this.searchEmployeeData();
      } else if (this.fetchDetails && !this.showForm) {
        this.showForm = true;
        this.displayEmployeeDetails();
      }
    }
    
    searchEmployeeData(): void {
      this.adminService.searchEmployee(this.email).subscribe(
        data => {
          this.searchResults = data;
          if (this.searchResults.length == 1) {
            const employee = this.searchResults[0];
            this.email = employee.emailAddress;
            this.username = `${employee.fullName}`;
            this.isUserVerified = true;
            this.fetchDetails = true;
            this.showForm = false;
            this.doaDetails = [];
            this.groupedDoaDetails = [];
          } else {
            this.sharedService.openModal('Message', 'Employee not found');
          }
          this.isLoading = false;
        },
        error => {
          console.error('Error fetching search results', error);
          this.isLoading = false;
        }
      );
    }
    
    displayEmployeeDetails(): void {
      this.adminService.searchEmployee(this.email).subscribe(
        employeeData => {
          this.employee = {
            employeeno: employeeData[0].employeeNo || '',
            name: employeeData[0].fullName || '',
            dept: employeeData[0].deptName || '',
            effectiveDate: ''
          };
          const empNo = this.employee.employeeno;
          if (empNo) {
            this.adminService.getEffectiveDate(empNo).subscribe(
              (date: any) => {
                this.effectiveDate = date?.effectiveDate1;
                this.initialEffectiveDate = this.effectiveDate;
              },
              error => {
                console.error('Error fetching effective date', error);
              }
            );
            this.adminService.getDoaDetails(empNo).subscribe(
              (details: DoaDetail[]) => {
                this.isLoading = false;
                this.doaDetails = details;
                this.groupedDoaDetails = this.groupBy(details, ['catName']);
                this.initialDoaDetails = JSON.parse(JSON.stringify(details)); 
              },
            );
          } else {
            this.isLoading = false;
            this.showError = true;
          }
        },
        error => {
          this.isLoading = false;
          console.error('Error fetching employee details', error);
        }
      );
      this.isUserVerified = false;
    }

  resetForm(): void {
    if (this.query) {
      this.displayEmployeeDetails();
    } else {
      this.query = '';
      this.employee = { employeeno: '', name: '', dept: '', effectiveDate: '' };
      this.searchResults = [];
      this.username = '';
      // this.isGoEnabled = false;
      this.isUserVerified = false;
      this.showForm = false;
      this.showError = false;
      this.fetchDetails = false;
      this.signatureUrl = '';
      this.doaDetails = [];
      this.groupedDoaDetails = [];
      this.effectiveDate = '';
      this.ELEMENT_DATA = [];
      this.empNo = '';
      this.invalidEntries = {};
      this.effectiveDateError = false;
    }

    this.groupedDoaDetails.forEach((group, i) => {
      group.types.forEach((type, j) => {
        const inputIndex = `${i}-${j}`;
        if (this.invalidEntries[inputIndex]) {
          this.invalidEntries[inputIndex] = false;
        }
      });
    });


  }

  validateDollarInput(event: any, index: string): void {
    const el = event.target;
    let val = el.value.trim().toUpperCase();
    const validValues = ['FA', 'NA', 'R', 'VAR', ''];
    const isValid = validValues.includes(val) || (!isNaN(Number(val.replace(/,/g, ''))) && Number(val.replace(/,/g, '')) >= 0);
    this.invalidEntries[index] = !isValid;
  
    if (isValid && !validValues.includes(val)) {
      val = Number(val.replace(/,/g, '')).toLocaleString('en-US');
      el.value = val;
    }
  }

  onSelectedResult(result: any): void {
    this.query = result.employeeNo;
    this.email = result.emailAddress;
    this.username = `${result.fullName}`;
    this.isUserVerified = true;
    this.fetchDetails = true;
    this.showForm = false;
    this.doaDetails = [];
    this.groupedDoaDetails = [];
  }
}